/**
 * Générateur — Attendu 659 — Propriété caractéristique de la médiatrice
 * d'un segment (équidistance `M ∈ méd([AB]) ⟺ MA = MB`).
 *
 * Niveau : 6ème | Cluster : 31 | Domaine : Espace et géométrie
 *
 * 5 exercices / 5 formats :
 *   - pc_mediatrice_sens_direct_saisie    : saisie numérique (3 sous-régimes)
 *                                            « M sur méd(AB), MA = x cm → MB ? »
 *   - pc_mediatrice_sens_reciproque_qcm   : QCM 4 choix (sans figure) sens
 *                                            réciproque « MA = MB ⟹ M ∈ méd »
 *   - pc_mediatrice_vf_figure_4points     : V/F (3V+3F stricte) + figure SVG
 *                                            (segment + médiatrice + 4 points
 *                                            dans 4 régimes distincts)
 *   - pc_mediatrice_vocabulaire_trous     : texte à trous (drag) — 5 modèles
 *                                            tous paramétrés par (A, B).
 *   - pc_mediatrice_construction_compas   : programme_construction 3 tâches
 *                                            (2 cercles de même rayon, puis la
 *                                            droite de leurs intersections).
 *
 * Variété cible : ≥ 20 variantes uniques par format (contrainte auditGen).
 * Voir docs/fiches/FICHE_659.md et docs/fiches/PAYLOADS_659.md.
 */

import {
    randInt, randomChoice, randomChoiceMultiple, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeProgrammeConstruction,
    subscriptToSvgTspan,
    filtrerDistracteurs,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 659;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_SAISIE = 'Sens direct PC (saisie)';
const L_QCM_REC = 'Sens réciproque PC (QCM)';
const L_VF_FIG  = 'Décision PC sur figure (V/F)';
const L_VOCAB   = 'Vocabulaire PC (texte à trous)';
const L_CONSTR  = 'Construction médiatrice au compas';

// Lettres valides — exclut I et O (ambiguïté avec 1, 0). Identique cluster 30.
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('');

// Pool de noms de droite (cluster 31)
const DROITES = ['(d)', '(Δ)', '(d₁)', '(ℓ)'];

// ViewBox pour Ex. 3 (figure)
const VIEWBOX = { w: 300, h: 220 };
const CENTER = { x: 150, y: 110 };

const ORIENTATIONS_DEG = [0, 30, 45, 60, 90, 120, 135, 150];

// Constantes programme_construction (Ex. 5) — alignées avec gen_657
const PX_PER_CM = 30;
const VIEWBOX_PC = { w: 600, h: 400 };
const TOLERANCE_PC = 12;
const COLOR_BLEU = '#378ADD';
const COLOR_VERT = '#1D9E75';

// ═══════════════════════════════════════════════════════════
// HELPERS COMMUNS
// ═══════════════════════════════════════════════════════════

/** Encode une chaîne SVG en base64, compatible navigateur + Node. */
const svgToBase64 = (svg) => {
    if (typeof btoa === 'function') {
        try { return btoa(unescape(encodeURIComponent(svg))); } catch (_e) { /* fallthrough */ }
    }
    return Buffer.from(svg, 'utf-8').toString('base64');
};

/** Tire n lettres distinctes dans un pool (par défaut LETTRES). */
const pickN = (n, pool = LETTRES) => pickLettresSains(pool, n);

/**
 * Dessine le petit carré d'angle droit (L ouvert) à l'intersection de deux
 * directions. Forme : polyline ouverte en 3 points (P1 → P2 → P3).
 */
const drawRightAngleMarker = (cx, cy, ux, uy, vx, vy, size = 8) => {
    const p1x = cx + ux * size, p1y = cy + uy * size;
    const p2x = p1x + vx * size, p2y = p1y + vy * size;
    const p3x = cx + vx * size, p3y = cy + vy * size;
    return `<polyline points="${p1x.toFixed(1)},${p1y.toFixed(1)} ${p2x.toFixed(1)},${p2y.toFixed(1)} ${p3x.toFixed(1)},${p3y.toFixed(1)}" fill="none" stroke="#000" stroke-width="1.2" />`;
};

/**
 * Dessine une marque de milieu (tiret centré, perpendiculaire au segment).
 */
const drawMidpointMark = (mx, my, nx, ny, len = 5) => {
    const x1 = mx - nx * len / 2, y1 = my - ny * len / 2;
    const x2 = mx + nx * len / 2, y2 = my + ny * len / 2;
    return `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="#000" stroke-width="1.2" stroke-linecap="round" />`;
};

/**
 * Étend une droite (passant par (px,py), de direction (dx,dy)) aux bords du
 * viewBox courant. Retourne {x1,y1,x2,y2}.
 */
const extendLineToViewBox = (px, py, dx, dy) => {
    const W = VIEWBOX.w, H = VIEWBOX.h;
    const ts = [];
    if (Math.abs(dx) > 1e-9) {
        ts.push((0 - px) / dx);
        ts.push((W - px) / dx);
    }
    if (Math.abs(dy) > 1e-9) {
        ts.push((0 - py) / dy);
        ts.push((H - py) / dy);
    }
    const pts = ts
        .map(t => ({ t, x: px + t * dx, y: py + t * dy }))
        .filter(p => p.x >= -0.5 && p.x <= W + 0.5 && p.y >= -0.5 && p.y <= H + 0.5)
        .sort((a, b) => a.t - b.t);
    if (pts.length < 2) {
        return { x1: 0, y1: py, x2: W, y2: py };
    }
    return { x1: pts[0].x, y1: pts[0].y, x2: pts[pts.length - 1].x, y2: pts[pts.length - 1].y };
};

// ═══════════════════════════════════════════════════════════
// Ex. 1 — Sens direct : calcul d'équidistance (saisie numérique)
// ═══════════════════════════════════════════════════════════

const VALUES_EX1 = [2, 3, 4, 5, 6, 7, 8, 10, 12];
const PREAMBULES_EX1 = [
    'Dans une construction géométrique, ',
    'D\'après le codage des longueurs sur la figure, ',
    'Sur une figure de géométrie, ',
];

/**
 * Figure du sens direct (cherry-pick MADELY, bilan prof D5, 2026-07-27) :
 * segment [AB] horizontal, M au-dessus, segments [MA]/[MB]. Le codage suit le
 * sous-régime : la médiatrice (droite pointillée + I + angle droit + tirets
 * sur [AI]/[IB]) est codée quand elle est l'HYPOTHÈSE (régimes a/c) ; l'égalité
 * MA = MB (tirets sur les segments) n'est codée qu'en régime b où
 * l'équidistance est donnée — la coder en a/c reviendrait à donner la réponse.
 */
const buildFigureSensDirectSvg = (A_name, B_name, M_name, cfg) => {
    const { mediatrice, codageEgal, labelMA, labelMB, d_name, demiBase, hauteurM } = cfg;
    const Ay = 172;
    const Ix = 150;
    const Ax = Ix - demiBase, Bx = Ix + demiBase;
    const My = Ay - hauteurM;
    const P = [];
    P.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VIEWBOX.w} ${VIEWBOX.h}" width="${VIEWBOX.w}" height="${VIEWBOX.h}">`);

    if (mediatrice) {
        // (d) est posée haut et à droite, l'étiquette de M bas et à gauche : sans
        // ça, avec hauteurM = 118 les deux se recouvraient (mesuré à 21,6 %).
        P.push(`<line x1="${Ix}" y1="16" x2="${Ix}" y2="${Ay + 30}" stroke="${COLOR_BLEU}" stroke-width="1.5" stroke-dasharray="7 5" />`);
        P.push(`<text x="${Ix + 7}" y="26" font-size="12" font-style="italic" fill="${COLOR_BLEU}" font-family="'Cambria Math','Times New Roman',serif">${subscriptToSvgTspan(d_name)}</text>`);
    }
    // Segment [AB] et les deux segments issus de M
    P.push(`<line x1="${Ax}" y1="${Ay}" x2="${Bx}" y2="${Ay}" stroke="#000" stroke-width="2" stroke-linecap="round" />`);
    P.push(`<line x1="${Ix}" y1="${My}" x2="${Ax}" y2="${Ay}" stroke="#000" stroke-width="1.8" />`);
    P.push(`<line x1="${Ix}" y1="${My}" x2="${Bx}" y2="${Ay}" stroke="#000" stroke-width="1.8" />`);

    if (mediatrice) {
        // Codage du milieu : angle droit en I + un tiret identique sur [AI] et [IB]
        P.push(drawRightAngleMarker(Ix, Ay, 0, -1, -1, 0, 9));
        P.push(drawMidpointMark((Ax + Ix) / 2, Ay, 0, 1, 8));
        P.push(drawMidpointMark((Bx + Ix) / 2, Ay, 0, 1, 8));
        P.push(`<circle cx="${Ix}" cy="${Ay}" r="2.6" fill="#000" />`);
        P.push(`<text x="${Ix - 4}" y="${Ay + 20}" font-size="12" text-anchor="end">I</text>`);
    }
    if (codageEgal) {
        // Deux tirets identiques : [MA] et [MB] ont la même longueur (hypothèse)
        [[Ax, Ay], [Bx, Ay]].forEach(([Xx, Xy]) => {
            const dx = Xx - Ix, dy = Xy - My;
            const n = Math.hypot(dx, dy) || 1;
            P.push(drawMidpointMark((Ix + Xx) / 2, (My + Xy) / 2, -dy / n, dx / n, 9));
        });
    }
    // Points et noms
    P.push(`<circle cx="${Ax}" cy="${Ay}" r="3" fill="#000" />`);
    P.push(`<circle cx="${Bx}" cy="${Ay}" r="3" fill="#000" />`);
    P.push(`<circle cx="${Ix}" cy="${My}" r="3" fill="#000" />`);
    P.push(`<text x="${Ax - 14}" y="${Ay + 6}" font-size="14" font-weight="bold">${A_name}</text>`);
    P.push(`<text x="${Bx + 6}" y="${Ay + 6}" font-size="14" font-weight="bold">${B_name}</text>`);
    P.push(`<text x="${Ix - 8}" y="${My - 9}" text-anchor="end" font-size="14" font-weight="bold">${M_name}</text>`);
    // Longueurs : la donnée d'un côté, l'inconnue de l'autre. Étiquettes
    // rejetées de part et d'autre (ancrage opposé) : centrées sur le milieu du
    // segment, elles chevauchaient le trait.
    P.push(`<text x="${(Ax + Ix) / 2 - 10}" y="${(Ay + My) / 2}" text-anchor="end" font-size="13" fill="${COLOR_VERT}" font-weight="600">${labelMA}</text>`);
    P.push(`<text x="${(Bx + Ix) / 2 + 10}" y="${(Ay + My) / 2}" text-anchor="start" font-size="13" fill="${COLOR_VERT}" font-weight="600">${labelMB}</text>`);
    P.push('</svg>');
    return P.join('');
};

const genPcSensDirectSaisie = () => {
    const sousRegime = randomChoice(['a', 'b', 'c']);
    const [A, B, M] = pickN(3);
    const x = randomChoice(VALUES_EX1);
    const preambule = randomChoice(PREAMBULES_EX1);
    const d_name = randomChoice(DROITES);
    const geom = { demiBase: randomChoice([70, 80, 90]), hauteurM: randomChoice([90, 105, 118]) };

    let texte;
    let reponse;
    let cfgFig;
    if (sousRegime === 'a') {
        texte =
            `<p>${preambule}le point ${M} est sur la médiatrice du segment [${A}${B}].</p>`
            + `<p>On sait que ${M}${A} = ${x} cm.</p>`
            + `<p>Combien vaut ${M}${B} ? (en cm)</p>`;
        reponse = String(x);
        cfgFig = { mediatrice: true, codageEgal: false, labelMA: `${x} cm`, labelMB: '? cm' };
    } else if (sousRegime === 'b') {
        texte =
            `<p>${preambule}le point ${M} est équidistant des points ${A} et ${B}.</p>`
            + `<p>Sachant que ${M}${B} = ${x} cm, combien vaut ${M}${A} ? (en cm)</p>`;
        reponse = String(x);
        cfgFig = { mediatrice: false, codageEgal: true, labelMA: '? cm', labelMB: `${x} cm` };
    } else {
        texte =
            `<p>${preambule}le point ${M} appartient à la médiatrice du segment [${A}${B}] et ${M}${A} = ${x} cm.</p>`
            + `<p>Quelle est la longueur totale ${M}${A} + ${M}${B} ? (en cm)</p>`;
        reponse = String(2 * x);
        // Régime c : PAS de « ? » sur [MB] — la question demande MA + MB (= 2x),
        // un « ? cm » sur [MB] induirait la saisie de x (ajustement 2026-07-27).
        cfgFig = { mediatrice: true, codageEgal: false, labelMA: `${x} cm`, labelMB: '' };
    }

    const svg = buildFigureSensDirectSvg(A, B, M, { ...cfgFig, ...geom, d_name });
    const payload = makeSaisie(reponse, texte, {
        ...opts(L_SAISIE),
        strict: false,
        suffixe: ' cm',
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml;base64,${svgToBase64(svg)}` } }],
        },
    });
    payload._meta.generatorKey = 'pc_mediatrice_sens_direct_saisie';
    payload._meta.params = { sousRegime, x };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 2 — Sens réciproque : localiser un point (QCM sans figure)
// ═══════════════════════════════════════════════════════════

/**
 * « Quelle affirmation est TOUJOURS vraie ? » et non « que peut-on en déduire ».
 * Le distracteur « M est le milieu de [AB] » décrit un cas POSSIBLE (le milieu
 * est bien équidistant de A et de B) : seule la quantification écarte
 * proprement les cas particuliers, sans quoi deux propositions se défendent.
 */
const ENONCES_EX2 = [
    (A, B, M) =>
        `<p>Soit ${M} un point tel que ${M}${A} = ${M}${B}.</p>`
        + `<p>Quelle affirmation est <strong>toujours vraie</strong> ?</p>`,
    (A, B, M) =>
        `<p>Un point ${M} vérifie : il est à égale distance de ${A} et de ${B}.</p>`
        + `<p>Quelle affirmation est <strong>toujours vraie</strong> ?</p>`,
];

const genPcSensReciproqueQcm = () => {
    const [A, B, M] = pickN(3);
    const enonce = randomChoice(ENONCES_EX2);
    const texte = enonce(A, B, M);

    const correct = `${M} appartient à la médiatrice du segment [${A}${B}].`;
    const distracteurs = [
        `${M} est le milieu du segment [${A}${B}].`,
        `${M} appartient à la droite (${A}${B}).`,
        `${M} est sur le cercle de centre ${A} passant par ${B}.`,
    ];
    const choix = [correct, ...distracteurs];

    const payload = makeQcm(choix, correct, texte, opts(L_QCM_REC));
    payload._meta.generatorKey = 'pc_mediatrice_sens_reciproque_qcm';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 3 — Décision sur figure codée (V/F + figure SVG, 4 points)
// ═══════════════════════════════════════════════════════════

/**
 * Tire la configuration géométrique : segment [AB], médiatrice (d),
 * et 4 points M1..M4 selon 4 régimes fixes (puis permutés pour brouiller
 * l'indice).
 *
 * Régimes :
 *   SM_loin       — sur (d), à 50–70 px du milieu
 *   SM_proche     — sur (d), à 15–30 px du milieu
 *   AB_cote_A     — sur [AB], à 10–25 px de A (côté A)
 *   ailleurs      — hors (d) et (AB), distance ≥ 30 px des deux droites
 *                   ET ≥ 40 px de A et B
 */
const REGIMES_EX3 = ['SM_loin', 'SM_proche', 'AB_cote_A', 'ailleurs'];

const tirerConfigFigureEx3 = () => {
    const thetaDeg = randomChoice(ORIENTATIONS_DEG);
    const thetaRad = thetaDeg * Math.PI / 180;
    const L = randInt(100, 140);
    const ux = Math.cos(thetaRad);
    const uy = Math.sin(thetaRad);
    const vx = -uy;
    const vy = ux;
    const Cx = CENTER.x, Cy = CENTER.y;
    const Ax = Cx - (L / 2) * ux;
    const Ay = Cy - (L / 2) * uy;
    const Bx = Cx + (L / 2) * ux;
    const By = Cy + (L / 2) * uy;
    const Ix = Cx, Iy = Cy;

    // Génère 4 points par régime. On retry jusqu'à ce qu'ils soient tous dans
    // le viewBox (marge 25) et espacés de ≥ 40 px les uns des autres.
    const maxTries = 80;
    for (let i = 0; i < maxTries; i++) {
        const signLoin = randomChoice([-1, 1]);
        const signProche = randomChoice([-1, 1]);
        const distLoin = randInt(50, 70);
        const distProche = randInt(15, 30);
        // SM_loin / SM_proche : le long de la médiatrice (direction perpendiculaire vx,vy)
        const pLoin = { x: Ix + signLoin * distLoin * vx, y: Iy + signLoin * distLoin * vy, regime: 'SM_loin' };
        const pProche = { x: Ix + signProche * distProche * vx, y: Iy + signProche * distProche * vy, regime: 'SM_proche' };
        // AB_cote_A : sur [AB], à 10–25 px de A (côté A)
        const distAB = randInt(10, 25);
        const pABcoteA = { x: Ax + distAB * ux, y: Ay + distAB * uy, regime: 'AB_cote_A' };
        // Ailleurs : hors (d) et (AB). Position tirée "off-grid" : offset u et v non nuls.
        // On tire offU ∈ [±(L/4, L/2)] (hors milieu) et offV ∈ [±(35, 55)] (hors (d))
        const signU = randomChoice([-1, 1]);
        const signV = randomChoice([-1, 1]);
        const offU = signU * randInt(Math.round(L / 4), Math.round(L / 2));
        const offV = signV * randInt(35, 55);
        const pAilleurs = { x: Cx + offU * ux + offV * vx, y: Cy + offU * uy + offV * vy, regime: 'ailleurs' };

        const candidats = [pLoin, pProche, pABcoteA, pAilleurs];
        // Contrainte viewBox
        const inViewBox = candidats.every(p =>
            p.x >= 25 && p.x <= VIEWBOX.w - 25 && p.y >= 25 && p.y <= VIEWBOX.h - 25
        );
        if (!inViewBox) continue;
        // Contrainte distance mutuelle ≥ 40 px
        let okDist = true;
        for (let j = 0; j < candidats.length && okDist; j++) {
            for (let k = j + 1; k < candidats.length && okDist; k++) {
                const d = Math.hypot(candidats[j].x - candidats[k].x, candidats[j].y - candidats[k].y);
                if (d < 40) okDist = false;
            }
        }
        if (!okDist) continue;
        // Contrainte pAilleurs distance à A et B ≥ 40 px
        const dAilleursA = Math.hypot(pAilleurs.x - Ax, pAilleurs.y - Ay);
        const dAilleursB = Math.hypot(pAilleurs.x - Bx, pAilleurs.y - By);
        if (dAilleursA < 40 || dAilleursB < 40) continue;
        // Contrainte pAilleurs distance à la médiatrice ≥ 30 px
        // (ux, uy) est la direction du segment [AB] (donc la NORMALE à la médiatrice).
        // La distance d'un point à la médiatrice = |projection sur la normale (ux, uy)|.
        const distMedAilleurs = Math.abs((pAilleurs.x - Ix) * ux + (pAilleurs.y - Iy) * uy);
        if (distMedAilleurs < 30) continue;
        // Contrainte pAilleurs distance à (AB) ≥ 20 px
        const distABAilleurs = Math.abs((pAilleurs.x - Cx) * vx + (pAilleurs.y - Cy) * vy);
        if (distABAilleurs < 20) continue;

        return { thetaRad, L, Ax, Ay, Bx, By, Ix, Iy, ux, uy, vx, vy, candidats };
    }
    // Fallback déterministe
    const fbTheta = 0;
    const fbL = 120;
    const fbUx = 1, fbUy = 0, fbVx = 0, fbVy = 1;
    return {
        thetaRad: fbTheta, L: fbL,
        Ax: 90, Ay: 110, Bx: 210, By: 110, Ix: 150, Iy: 110,
        ux: fbUx, uy: fbUy, vx: fbVx, vy: fbVy,
        candidats: [
            { x: 150, y: 50,  regime: 'SM_loin' },
            { x: 150, y: 95,  regime: 'SM_proche' },
            { x: 105, y: 110, regime: 'AB_cote_A' },
            { x: 70,  y: 180, regime: 'ailleurs' },
        ],
    };
};

/**
 * Place une étiquette autour d'un point sans recouvrir celles déjà posées.
 * Essaie quatre positions (haut-droite, haut-gauche, bas-droite, bas-gauche) et
 * retient la première libre et dans le cadre. Les étiquettes sont modélisées par
 * une boîte, en tenant compte de `text-anchor` : sans ça, l'étiquette du point
 * placé près de A recouvrait celle de A dans 46,6 % des tirages.
 */
const boiteLabel = (x, y, largeur, anchor) => ({
    x0: anchor === 'end' ? x - largeur : x,
    x1: anchor === 'end' ? x : x + largeur,
    y0: y - 11,
    y1: y + 3,
});

const seChevauchent = (a, b) => a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;

const placerLabel = (px, py, largeur, dejaPosees) => {
    const options = [
        { dx: 9, dy: -8, anchor: 'start' },
        { dx: -9, dy: -8, anchor: 'end' },
        { dx: 9, dy: 17, anchor: 'start' },
        { dx: -9, dy: 17, anchor: 'end' },
    ];
    for (const o of options) {
        const x = px + o.dx, y = py + o.dy;
        const b = boiteLabel(x, y, largeur, o.anchor);
        if (b.x0 < 2 || b.x1 > VIEWBOX.w - 2 || b.y0 < 2 || b.y1 > VIEWBOX.h - 2) continue;
        if (dejaPosees.some(q => seChevauchent(b, q))) continue;
        return { x, y, anchor: o.anchor, boite: b };
    }
    const x = px + 9, y = py - 8;
    return { x, y, anchor: 'start', boite: boiteLabel(x, y, largeur, 'start') };
};

/** Construit le SVG figure Ex. 3 : segment + médiatrice + 4 points M_i codés. */
const buildFigureEx3Svg = (cfg, A_name, B_name, d_name, permutation) => {
    const { Ax, Ay, Bx, By, Ix, Iy, ux, uy, vx, vy, candidats } = cfg;
    // Segment
    const svgParts = [];
    svgParts.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VIEWBOX.w} ${VIEWBOX.h}" width="${VIEWBOX.w}" height="${VIEWBOX.h}">`);
    // Médiatrice étendue (direction perpendiculaire au segment : (vx, vy))
    const { x1, y1, x2, y2 } = extendLineToViewBox(Ix, Iy, vx, vy);
    svgParts.push(`<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="#000" stroke-width="1.5" />`);
    // Segment [AB]
    svgParts.push(`<line x1="${Ax.toFixed(1)}" y1="${Ay.toFixed(1)}" x2="${Bx.toFixed(1)}" y2="${By.toFixed(1)}" stroke="#000" stroke-width="2" stroke-linecap="round" />`);
    // Disques + labels A, B
    svgParts.push(`<circle cx="${Ax.toFixed(1)}" cy="${Ay.toFixed(1)}" r="3" fill="#000" />`);
    svgParts.push(`<circle cx="${Bx.toFixed(1)}" cy="${By.toFixed(1)}" r="3" fill="#000" />`);
    const aLabelX = Ax - ux * 12 - vx * 6;
    const aLabelY = Ay - uy * 12 - vy * 6 + 4;
    const bLabelX = Bx + ux * 12 + vx * 6;
    const bLabelY = By + uy * 12 + vy * 6 + 4;
    svgParts.push(`<text x="${aLabelX.toFixed(1)}" y="${aLabelY.toFixed(1)}" font-size="14" font-weight="bold">${A_name}</text>`);
    svgParts.push(`<text x="${bLabelX.toFixed(1)}" y="${bLabelY.toFixed(1)}" font-size="14" font-weight="bold">${B_name}</text>`);
    // Codage médiatrice : carré d'angle droit à I + 2 tirets identiques au milieu
    // de [AI] et [IB] (convention "I est le milieu de [AB]").
    svgParts.push(drawRightAngleMarker(Ix, Iy, vx, vy, ux, uy, 8));
    const halfAx = (Ax + Ix) / 2, halfAy = (Ay + Iy) / 2;
    const halfBx = (Bx + Ix) / 2, halfBy = (By + Iy) / 2;
    svgParts.push(drawMidpointMark(halfAx, halfAy, vx, vy, 6));
    svgParts.push(drawMidpointMark(halfBx, halfBy, vx, vy, 6));
    // Label médiatrice
    const useFirst = (y1 <= y2);
    const labelBaseX = useFirst ? x1 : x2;
    const labelBaseY = useFirst ? y1 : y2;
    const dLabelX = Math.max(4, Math.min(VIEWBOX.w - 30, labelBaseX + 4));
    const dLabelY = Math.max(14, Math.min(VIEWBOX.h - 4, labelBaseY + 14));
    svgParts.push(`<text x="${dLabelX.toFixed(1)}" y="${dLabelY.toFixed(1)}" font-size="13" font-style="italic" font-family="'Cambria Math','Times New Roman',serif">${subscriptToSvgTspan(d_name)}</text>`);
    // Étiquettes déjà posées : A, B et la droite. Les Mᵢ devront les contourner.
    const posees = [
        boiteLabel(aLabelX, aLabelY, 11, 'start'),
        boiteLabel(bLabelX, bLabelY, 11, 'start'),
        boiteLabel(dLabelX, dLabelY, 28, 'start'),
    ];
    // 4 points M_1..M_4 selon la permutation (permutation[i] = index dans candidats)
    for (let i = 0; i < 4; i++) {
        const p = candidats[permutation[i]];
        svgParts.push(`<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="3" fill="#000" />`);
        const lab = placerLabel(p.x, p.y, 20, posees);
        posees.push(lab.boite);
        svgParts.push(`<text x="${lab.x.toFixed(1)}" y="${lab.y.toFixed(1)}" text-anchor="${lab.anchor}" font-size="13" font-weight="bold">${subscriptToSvgTspan(`M${'₀₁₂₃₄'[i + 1]}`)}</text>`);
        // Pas de codage d'égalité MA = MB sur les points M_i : l'élève doit le déduire
        // de la position du point par rapport à la médiatrice (c'est le but de l'exo).
    }
    svgParts.push('</svg>');
    return svgParts.join('');
};

/** Étiquette M en indice Unicode (M₁…M₄) — même notation que la figure SVG. */
const Mi = (i) => `M${'₀₁₂₃₄'[i]}`;

const genPcVfFigure4Points = () => {
    const cfg = tirerConfigFigureEx3();
    // M exclu du tirage : les points de la figure s'appellent M₁…M₄ — un
    // segment [M…] rendrait « M₁M = M₁B » illisible pour un 6e.
    const [A, B] = pickN(2, LETTRES.filter((l) => l !== 'M'));
    const d_name = randomChoice(DROITES);

    // Permutation aléatoire des 4 points (4! = 24 permutations) pour brouiller l'index
    const permutation = shuffle([0, 1, 2, 3]);

    // Helper : récupère le régime pour l'étiquette M_i (1-indexed)
    const regimePourM = (i1) => cfg.candidats[permutation[i1 - 1]].regime;
    const estSurMediatrice = (i1) => {
        const r = regimePourM(i1);
        return r === 'SM_loin' || r === 'SM_proche';
    };
    const estSurAB = (i1) => regimePourM(i1) === 'AB_cote_A';
    const distMA = (i1) => {
        const p = cfg.candidats[permutation[i1 - 1]];
        return Math.hypot(p.x - cfg.Ax, p.y - cfg.Ay);
    };

    // Pool d'affirmations avec classification V/F dynamique
    const affirmations = [];
    for (let i = 1; i <= 4; i++) {
        const onMed = estSurMediatrice(i);
        affirmations.push({
            text: `${Mi(i)}${A} = ${Mi(i)}${B}`,
            isTrue: onMed,
        });
        affirmations.push({
            text: `${Mi(i)} appartient à la médiatrice de [${A}${B}].`,
            isTrue: onMed,
        });
    }
    // Affirmations globales
    const kSurAB = [1, 2, 3, 4].find(i => estSurAB(i));
    const kAilleurs = [1, 2, 3, 4].find(i => regimePourM(i) === 'ailleurs');
    affirmations.push({
        text: `Il existe au moins un point parmi M₁, M₂, M₃, M₄ qui est équidistant de ${A} et ${B}.`,
        isTrue: true,
    });
    affirmations.push({
        text: `Tous les points M₁, M₂, M₃, M₄ sont équidistants de ${A} et ${B}.`,
        isTrue: false,
    });
    affirmations.push({
        text: `Aucun des points M₁, M₂, M₃, M₄ n'est sur la médiatrice de [${A}${B}].`,
        isTrue: false,
    });
    affirmations.push({
        text: `Les quatre points M₁, M₂, M₃, M₄ appartiennent à la droite (${A}${B}).`,
        isTrue: false,
    });
    // « M_k n'est pas sur la médiatrice » où M_k ∈ {AB_cote_A, ailleurs} : V
    if (kSurAB) {
        affirmations.push({
            text: `${Mi(kSurAB)} n'est pas sur la médiatrice de [${A}${B}].`,
            isTrue: true,
        });
    }
    if (kAilleurs) {
        affirmations.push({
            text: `${Mi(kAilleurs)} n'est pas sur la médiatrice de [${A}${B}].`,
            isTrue: true,
        });
    }
    // « M_k est le milieu de [AB] » — jamais vrai : aucun régime ne place le point
    // exactement au milieu (SM_proche est placé à ≥15 px du milieu, les autres régimes
    // sont hors médiatrice ou hors segment).
    for (let i = 1; i <= 4; i++) {
        affirmations.push({
            text: `${Mi(i)} est le milieu du segment [${A}${B}].`,
            isTrue: false,
        });
    }
    // « M_k appartient au cercle de centre A passant par B » : rayon du cercle = AB.
    // Génération conditionnelle — on n'ajoute l'affirmation (comme FAUSSE) que si
    // |M_iA − AB| > 8 px, pour éviter une ambiguïté due au positionnement aléatoire.
    // La dédup + le tirage de 3F distincts garantissent un pool F suffisant.
    const AB_px = Math.hypot(cfg.Ax - cfg.Bx, cfg.Ay - cfg.By);
    for (let i = 1; i <= 4; i++) {
        const dMA = distMA(i);
        if (Math.abs(dMA - AB_px) > 8) {
            affirmations.push({
                text: `${Mi(i)} appartient au cercle de centre ${A} passant par ${B}.`,
                isTrue: false,
            });
        }
    }

    // Dédupliquer par texte (sécurité : certaines affirmations dupliquées si régime rare)
    const seen = new Set();
    const uniq = [];
    for (const a of affirmations) {
        if (!seen.has(a.text)) { seen.add(a.text); uniq.push(a); }
    }
    const poolV = uniq.filter(a => a.isTrue);
    const poolF = uniq.filter(a => !a.isTrue);

    // Tirer 3V + 3F distincts (toujours possible par construction : on a ≥ 3 dans chaque pool)
    const chosenV = randomChoiceMultiple(poolV, 3).map(a => a.text);
    const chosenF = randomChoiceMultiple(poolF, 3).map(a => a.text);

    // SHUFFLE côté générateur (OBLIGATOIRE)
    const merged = shuffle([
        ...chosenV.map(l => ({ ligne: l, reponse: 0 })),
        ...chosenF.map(l => ({ ligne: l, reponse: 1 })),
    ]);
    const lignes = merged.map(x => x.ligne);
    const bonnesReponses = merged.map(x => x.reponse);

    const svg = buildFigureEx3Svg(cfg, A, B, d_name, permutation);
    const imageUrl = `data:image/svg+xml;base64,${svgToBase64(svg)}`;

    const texte = `<p>${d_name} est la médiatrice du segment [${A}${B}]. Quatre points sont nommés sur la figure.</p>`;

    const payload = makeVraiFaux(lignes, bonnesReponses, texte, {
        ...opts(L_VF_FIG),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageUrl } }],
        },
    });
    payload._meta.generatorKey = 'pc_mediatrice_vf_figure_4points';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 4 — Verbalisation de la PC (texte à trous)
// ═══════════════════════════════════════════════════════════

// 5 modèles — tous paramétrés par (A, B) pour garantir l'unicité via fingerprint phrase.
//
// Distracteurs PAR MODÈLE (bilan prof D6, arbitré 2026-07-27) : chaque étiquette
// est grammaticalement substituable dans au moins un trou du modèle ET matérialise
// une confusion réelle — l'élimination par l'accord n'est plus possible.
// Convention « (s) » : toute étiquette dont le nombre varie s'écrit en forme
// neutre (« symétrique(s) »), BONNES RÉPONSES COMPRISES (sinon leur accord les
// trahirait par contraste) ; le genre, lui, ne se neutralise pas — on ne met en
// concurrence sur un même trou que des mots du même genre.
// ⚠️ pas de symboles à caractère combinant : "≠" se décompose en "=" + barre
// (U+0338) que le scoring efface → "≠" devenait équivalent au mot "=".
const MODELES_VOCAB = [
    {
        id: 'A',
        build: (A, B) => ({
            phrase: `La médiatrice du segment [${A}${B}] est l'ensemble des points {{équidistant(s)}} des deux {{extrémité(s)}} du segment.`,
            bonnes: ['équidistant(s)', 'extrémité(s)'],
        }),
        // « symétrique(s) » : LE piège du chapitre (médiatrice introduite via
        // la symétrie axiale — A et B sont symétriques par rapport à elle).
        distracteurs: ['symétrique(s)', 'aligné(s)', 'côté(s)', 'centre(s)', 'sommet(s)'],
    },
    {
        id: 'B',
        build: (A, B) => ({
            phrase: `Si un point M appartient à la médiatrice de [${A}${B}], alors M${A} et M${B} sont {{égale(s)}} : M${A} {{=}} M${B}.`,
            bonnes: ['égale(s)', '='],
        }),
        // « différente(s) » = la négation exacte, légitime ici.
        distracteurs: ['différente(s)', 'symétrique(s)', 'perpendiculaire(s)', '<', '>', '+'],
    },
    {
        id: 'C',
        build: (A, B) => ({
            phrase: `Si un point M vérifie M${A} = M${B}, alors M appartient à la {{médiatrice}} du {{segment}} [${A}${B}].`,
            bonnes: ['médiatrice', 'segment'],
        }),
        // Trou 1 : le quatuor de confusion classique (tout féminin singulier).
        distracteurs: ['bissectrice', 'médiane', 'hauteur', 'milieu', 'cercle', 'triangle'],
    },
    {
        id: 'D',
        build: (A, B) => ({
            phrase: `Pour construire la médiatrice de [${A}${B}] au compas, on trace deux {{arc(s)}} de même {{rayon}}, l'un centré en ${A}, l'autre centré en ${B}.`,
            bonnes: ['arc(s)', 'rayon'],
        }),
        // « centre » : erreur conceptuelle réelle (les deux arcs n'ont PAS le
        // même centre). « longueur » INTERDIT : « deux arcs de même longueur »
        // serait mathématiquement acceptable → ambiguïté.
        distracteurs: ['cercle(s)', 'segment(s)', 'diamètre', 'centre'],
    },
    {
        id: 'E',
        build: (A, B) => ({
            phrase: `La propriété caractéristique de la médiatrice de [${A}${B}] relie une condition {{géométrique}} (être sur la médiatrice) à une condition de {{distance}} (être équidistant de ${A} et ${B}).`,
            bonnes: ['géométrique', 'distance'],
        }),
        distracteurs: ['algébrique', 'numérique', 'position', 'mesure'],
    },
];

const genPcVocabulaireTrous = () => {
    const modele = randomChoice(MODELES_VOCAB);
    // M exclu du tirage (fix 2026-07-28, constaté en quiz élève) : les modèles
    // B et C parlent d'un point littéral « M » — tirer M comme extrémité
    // donnait « médiatrice de [MS] … MM = MS », absurde (MM = 0).
    const [A, B] = pickN(2, LETTRES.filter((l) => l !== 'M'));
    const { phrase, bonnes } = modele.build(A, B);

    // Distracteurs : 3–4 étiquettes tirées dans le pool DU MODÈLE, filtrées
    // contre les mots attendus (équivalence normalisée du scoring)
    const poolDistincts = filtrerDistracteurs(phrase, modele.distracteurs);
    const nbDistracteurs = Math.min(randInt(3, 4), poolDistincts.length);
    const distracteurs = randomChoiceMultiple(poolDistincts, nbDistracteurs);

    // Texte OBLIGATOIRE (le serializer back refuse un énoncé vide au POST —
    // revue 2026-08-11) : une consigne d'une ligne, la phrase à trous reste
    // le coeur du format.
    const texte = '<p>Complète la phrase en glissant les étiquettes.</p>';

    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_VOCAB),
        mode: 'drag',
        distracteurs,
        strict: false,
    });
    payload._meta.generatorKey = 'pc_mediatrice_vocabulaire_trous';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 5 — Construction au compas (programme_construction)
// ═══════════════════════════════════════════════════════════

/** Tire une configuration valide (A, B, AB_cm, r_cm) telle que r > AB/2 et arcs dans viewBox. */
const tirerConfigEx5 = () => {
    const maxTries = 200;
    // 25 et non 50 : avec 50, `randInt(margin + r_px, H - margin - r_px)` donnait
    // un intervalle d'une seule valeur pour r = 5 cm, d'où 4 % de tirages pour
    // ces paires contre 71 % pour les deux plus petites.
    const margin = 25;
    const W = VIEWBOX_PC.w;
    const H = VIEWBOX_PC.h;
    const bboxMargin = 12;

    // Paires (AB, r) valides : r > AB/2 + 0.5, ET cercle inscriptible dans la
    // zone. Les paires r = 6 cm ont été retirées : 360 px de diamètre ne tiennent
    // pas dans 400 px de haut, elles ne sortaient jamais (mesuré à 0 sur 5000).
    const pairesValides = [
        { AB: 4, r: 3 }, { AB: 4, r: 4 }, { AB: 4, r: 5 },
        { AB: 6, r: 4 }, { AB: 6, r: 5 },
        { AB: 8, r: 5 },
    ];

    for (let i = 0; i < maxTries; i++) {
        const { AB: ABcm, r: rCm } = randomChoice(pairesValides);
        const AB_px = ABcm * PX_PER_CM;
        const r_px = rCm * PX_PER_CM;

        const thetaDeg = randomChoice(ORIENTATIONS_DEG);
        const thetaRad = thetaDeg * Math.PI / 180;
        const ux = Math.cos(thetaRad);
        const uy = Math.sin(thetaRad);

        // Centre de [AB] tiré dans la zone viable (avec marge suffisante pour les arcs)
        const Cx = randInt(margin + r_px, W - margin - r_px);
        const Cy = randInt(margin + r_px, H - margin - r_px);
        if (Cx < margin + r_px || Cx > W - margin - r_px) continue;
        if (Cy < margin + r_px || Cy > H - margin - r_px) continue;

        const Ax = Cx - (AB_px / 2) * ux;
        const Ay = Cy - (AB_px / 2) * uy;
        const Bx = Cx + (AB_px / 2) * ux;
        const By = Cy + (AB_px / 2) * uy;

        // Les 2 arcs doivent rester dans le viewBox
        if (Ax - r_px < bboxMargin || Ax + r_px > W - bboxMargin) continue;
        if (Ay - r_px < bboxMargin || Ay + r_px > H - bboxMargin) continue;
        if (Bx - r_px < bboxMargin || Bx + r_px > W - bboxMargin) continue;
        if (By - r_px < bboxMargin || By + r_px > H - bboxMargin) continue;

        return { Ax, Ay, Bx, By, ABcm, rCm, r_px };
    }
    // Fallback déterministe (AB=6 cm horizontal, r=5 cm)
    // Vérif marges : Ax-r_px=60, Bx+r_px=540 (≤ 588 = W-bboxMargin), Ay±r_px∈[50,350] (≤ 388 = H-bboxMargin). OK.
    return { Ax: 210, Ay: 200, Bx: 390, By: 200, ABcm: 6, rCm: 5, r_px: 150 };
};

const genPcConstructionCompas = () => {
    const [A_name, B_name] = pickN(2);
    const cfg = tirerConfigEx5();

    const points = [
        { name: A_name, x: cfg.Ax, y: cfg.Ay },
        { name: B_name, x: cfg.Bx, y: cfg.By },
    ];

    const preShapes = [
        { type: 'line', from: A_name, to: B_name },
    ];

    // Calcul des 2 points d'intersection des 2 arcs (cercles de même rayon r
     // centrés en A et B). Les intersections sont sur la médiatrice, à distance
     // h = √(r² - (AB/2)²) du milieu I de [AB], de part et d'autre de (AB).
    const AB_px = Math.hypot(cfg.Bx - cfg.Ax, cfg.By - cfg.Ay);
    const Ix = (cfg.Ax + cfg.Bx) / 2;
    const Iy = (cfg.Ay + cfg.By) / 2;
    const h = Math.sqrt(Math.max(0, cfg.r_px ** 2 - (AB_px / 2) ** 2));
    // Vecteur unitaire perpendiculaire à AB
    const vx = -(cfg.By - cfg.Ay) / AB_px;
    const vy = (cfg.Bx - cfg.Ax) / AB_px;
    const inter1 = { x: Ix + h * vx, y: Iy + h * vy };
    const inter2 = { x: Ix - h * vx, y: Iy - h * vy };

    const tasks = [
        {
            id: 'a',
            consigne: `Trace en <b>bleu</b> le cercle de centre ${A_name} et de rayon ${cfg.rCm} cm.`,
            color: COLOR_BLEU,
            shape: { type: 'circle', cx: cfg.Ax, cy: cfg.Ay, r: cfg.r_px },
        },
        {
            id: 'b',
            consigne: `Trace en <b>bleu</b> le cercle de centre ${B_name} et de <b>même rayon</b> (${cfg.rCm} cm).`,
            color: COLOR_BLEU,
            shape: { type: 'circle', cx: cfg.Bx, cy: cfg.By, r: cfg.r_px },
        },
        {
            id: 'c',
            kind: 'line',
            consigne: `Trace en <b>vert</b> la droite passant par les <b>deux points d'intersection</b> des deux cercles.`,
            color: COLOR_VERT,
            shape: { type: 'line', p1: inter1, p2: inter2 },
        },
    ];

    // Pas de justification dans l'énoncé : « la droite passant par les deux
    // intersections est la médiatrice parce que ses points sont équidistants »
    // est exactement ce que la construction doit faire découvrir.
    const texte = `<p>Construis la médiatrice de [${A_name}${B_name}] au compas.</p>`;

    const payload = makeProgrammeConstruction(
        {
            pxPerCm: PX_PER_CM,
            tolerance: TOLERANCE_PC,
            viewBox: VIEWBOX_PC,
            points,
            preShapes,
            tasks,
        },
        texte,
        opts(L_CONSTR),
    );
    payload._meta.generatorKey = 'pc_mediatrice_construction_compas';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    pc_mediatrice_sens_direct_saisie:  genPcSensDirectSaisie,
    pc_mediatrice_sens_reciproque_qcm: genPcSensReciproqueQcm,
    pc_mediatrice_vf_figure_4points:   genPcVfFigure4Points,
    pc_mediatrice_vocabulaire_trous:   genPcVocabulaireTrous,
    pc_mediatrice_construction_compas: genPcConstructionCompas,
};

export const CATEGORIES = [
    { id: 'pc_sens_direct',     label: L_SAISIE,  formatsList: [{ key: 'pc_mediatrice_sens_direct_saisie',  label: 'Saisie' }] },
    { id: 'pc_sens_reciproque', label: L_QCM_REC, formatsList: [{ key: 'pc_mediatrice_sens_reciproque_qcm', label: 'QCM' }] },
    { id: 'pc_vf_figure',       label: L_VF_FIG,  formatsList: [{ key: 'pc_mediatrice_vf_figure_4points',   label: 'Vrai/Faux' }] },
    { id: 'pc_vocabulaire',     label: L_VOCAB,   formatsList: [{ key: 'pc_mediatrice_vocabulaire_trous',   label: 'Texte à trous' }] },
    { id: 'pc_construction',    label: L_CONSTR,  formatsList: [{ key: 'pc_mediatrice_construction_compas', label: 'Construction' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genPcSensDirectSaisie,
    genPcSensReciproqueQcm,
    genPcVfFigure4Points,
    genPcVocabulaireTrous,
    genPcConstructionCompas,
};
